/**
 * KYCWorkspace Component
 * TASK-373: AI-Assisted KYC Filling Workspace
 * TASK-716: the left chat panel is DELETED — the dock (scope `KYC · <client>`)
 * carries the conversation; the form is full-width. Document review surfaces
 * (initial KG review, staged transitions, CO review) render in a review strip
 * above the form. One-component-two-mounts: route shell
 * `/clients/:id/kyc-workspace` (deep links) + dashboard focus popup.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  FileText,
  PanelRightClose,
  PanelRightOpen,
  Sparkles,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { httpClient, httpClientLongRunning } from '../../../lib/httpClient';
import { logger } from '../../../utils/logger';
import { getClientKnowledge, batchVerifyEntries } from '../../../services/knowledgeGraphService';
import {
  listStagedProposals,
  acceptStagedProposal,
  dismissStagedProposal,
  type StagedProposal,
} from '../../../services/kycProposalService';
import { getReviewByProfile } from '../../../services/reviewService';
import {
  partitionEntriesByVerification,
  mapKGFieldToFormPath,
  cleanKGValue,
} from '../../../utils/kgFieldMapping';
import type { KnowledgeGraphEntry, KYCReview, KYCReviewComment } from '../../../types/complianceWorkflow';
import { useKYCBridge } from '../../../stores/kycBridge';
import { useClientExtractionStatus } from '../../../hooks/useClientExtractionStatus';
import { useDockStore } from '../../../stores/dockStore';
import { requestCopilotPrompt } from '../../dock/requestCopilotPrompt';
import { useFocusStore } from '../../../stores/focusStore';

import SecondaryNav from '../../layout/SecondaryNav';
import DocumentViewerPanel from './DocumentViewerPanel';
import KYCFormPanel from './KYCFormPanel';
import InitialProposalReview from './InitialProposalReview';
import TransitionProposalCard from './TransitionProposalCard';
import FieldProposalCard from './FieldProposalCard';
import TranscriptEvidencePanel from '../../provenance/TranscriptEvidencePanel';
import COReviewTab from './COReviewTab';

// TASK-474 shipped this affordance in `KYCChatPanel`; TASK-708's dock consolidation
// deleted that panel and ported its review-strip handlers but not its "Derive DNA"
// button, so the KYC surface lost its only trigger for the derivation. Approving
// extractions verifies KYC facts (`personal.*`, `employment.*`, `background.*`) —
// DNA is the `values.*` layer DERIVED from those facts, so with nothing left to run
// the derivation an approved document could never reach the DNA card, which meanwhile
// still told the RM to press the deleted button. Verbatim from the deleted panel so
// the tool routes exactly as it did before.
const DERIVE_DNA_PROMPT =
  "Derive this client's DNA (values, hard exclusions, tilts, promises, " +
  'life events, temperament) from their knowledge graph.';

// ============================================================================
// Component
// ============================================================================

export interface KYCWorkspaceProps {
  clientId?: string;
  host?: 'route' | 'focus';
  /** Focus host: the client is invalid/unauthorized — show the neutral state. */
  onInvalid?: () => void;
}

const KYCWorkspace: React.FC<KYCWorkspaceProps> = ({
  clientId: clientIdProp,
  host = 'route',
  onInvalid,
}) => {
  const { clientId: routeClientId } = useParams<{ clientId: string }>();
  const clientId = clientIdProp ?? routeClientId;
  const navigate = useNavigate();
  const location = useLocation();

  // Hand a prompt to whichever copilot is actually mounted here: the global Dock
  // on the workspace route, the canvas InputDock when this renders as the dashboard
  // focus popup (the Dock unmounts there). Handing it to the wrong store is a silent
  // no-op, so the placement rule lives in one shared helper (TASK-816).
  const requestPrompt = useCallback(
    (prompt: string) => {
      if (!requestCopilotPrompt(prompt, location.pathname)) {
        toast.error('Open the copilot here to continue this in chat');
      }
    },
    [location.pathname]
  );

  // Data state
  const [knowledgeEntries, setKnowledgeEntries] = useState<
    KnowledgeGraphEntry[]
  >([]);
  const [documentIds, setDocumentIds] = useState<string[]>([]);
  const [clientName, setClientName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // TASK-617: staged proposals (transitions + meeting-fed fields) for the rail
  const [stagedProposals, setStagedProposals] = useState<StagedProposal[]>([]);
  const [transitionAffects, setTransitionAffects] = useState<Record<string, string[]>>({});

  // Refetch KG + staged proposals after an accept/dismiss so the form,
  // history timeline and cards all reflect the applied cascade.
  const refreshKnowledge = useCallback(async () => {
    if (!clientId) return;
    const [kg, staged] = await Promise.allSettled([
      getClientKnowledge(clientId),
      listStagedProposals(clientId),
    ]);
    if (kg.status === 'fulfilled') setKnowledgeEntries(kg.value);
    if (staged.status === 'fulfilled') {
      setStagedProposals(staged.value.proposals);
      setTransitionAffects(staged.value.transition_affects);
    }
  }, [clientId]);

  // TASK-817: the documents this client has in extraction right now.
  //
  // Opening this workspace seconds after accepting a document used to show an empty record
  // with no explanation — the knowledge graph is fetched once on mount, and the fields were
  // still being read. The poll both surfaces that work and re-reads the graph when it lands,
  // so the fields appear without the user having to guess that a refresh would help.
  const [retryingIds, setRetryingIds] = useState<string[]>([]);

  const {
    status: extractionStatus,
    retry: retryExtraction,
  } = useClientExtractionStatus(clientId, refreshKnowledge);

  const handleRetryExtraction = useCallback(
    async (documentId: string) => {
      setRetryingIds((ids) => [...ids, documentId]);
      try {
        await retryExtraction(documentId);
        toast.success('Reading the document again');
      } catch (err) {
        // 409 is not a failure — the extraction the user is trying to restart is genuinely
        // running. Saying "failed" there would be the same lie in the other direction.
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          toast.info('That document is already being read');
        } else {
          logger.error('[KYCWorkspace] Extraction retry failed:', err);
          toast.error('Could not restart the extraction');
        }
      } finally {
        setRetryingIds((ids) => ids.filter((id) => id !== documentId));
      }
    },
    [retryExtraction]
  );

  // CO review state (for changes_requested profiles)
  const [profileStatus, setProfileStatus] = useState<string | null>(null);
  const [latestReview, setLatestReview] = useState<KYCReview | null>(null);
  const [reviewComments, setReviewComments] = useState<KYCReviewComment[]>([]);

  // TASK-595: KYC dossier generation (KG → first-party .docx via document-export)
  const [generatingDossier, setGeneratingDossier] = useState(false);
  const generateDossier = useCallback(async () => {
    if (!clientId || generatingDossier) return;
    setGeneratingDossier(true);
    try {
      const res = await httpClientLongRunning.post<{
        document_id: string;
        version: number;
        url: string;
        summary: { total: number; mapped: number; missing: number; ambiguous: number };
      }>('/api/workbench/documents/kyc-dossier', { client_id: clientId });
      const { summary } = res;
      toast.success(
        `KYC dossier v${res.version} generated — ${summary.mapped}/${summary.total} fields filled` +
          (summary.missing ? `, ${summary.missing} missing in the KG` : ''),
      );
      window.open(res.url, '_blank', 'noopener');
    } catch (e) {
      logger.error('KYC dossier generation failed', e);
      toast.error('Could not generate the KYC dossier');
    } finally {
      setGeneratingDossier(false);
    }
  }, [clientId, generatingDossier]);

  const [showDocViewer, setShowDocViewer] = useState(false);
  // TASK-716: the review strip replaces the left panel's review surfaces.
  const [reviewStripOpen, setReviewStripOpen] = useState(true);

  // KYC bridge reset on unmount (the dock's kyc-scope thread survives — the
  // bridge holds only cross-panel wiring).
  useEffect(() => {
    return () => {
      useKYCBridge.getState().reset();
    };
  }, []);

  // ============================================================================
  // Data Fetching
  // ============================================================================

  useEffect(() => {
    if (!clientId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch knowledge graph entries, client info, and profile in parallel
        const [kgEntries, clientInfo, profileInfo, staged] = await Promise.allSettled([
          getClientKnowledge(clientId),
          httpClient.get<{ name: string; document_ids?: string[] }>(
            `/api/clients/${clientId}`
          ),
          httpClient.get<{ data: { id: string; status: string } }>(
            `/api/kyc/profiles/${clientId}`
          ),
          listStagedProposals(clientId),
        ]);

        if (staged.status === 'fulfilled') {
          setStagedProposals(staged.value.proposals);
          setTransitionAffects(staged.value.transition_affects);
        }

        if (kgEntries.status === 'fulfilled') {
          setKnowledgeEntries(kgEntries.value);
        } else {
          logger.warn(
            '[KYCWorkspace] Failed to load knowledge graph:',
            kgEntries.reason
          );
        }

        if (clientInfo.status === 'fulfilled') {
          setClientName(clientInfo.value.name || 'Unknown Client');
          setDocumentIds(clientInfo.value.document_ids || []);
        } else {
          logger.warn(
            '[KYCWorkspace] Failed to load client info:',
            clientInfo.reason
          );
          // Focus host: an unloadable client is the popup's invalid state.
          if (host === 'focus') {
            onInvalid?.();
            return;
          }
          setClientName('Unknown Client');
        }

        // TASK-602: a rejected profile fetch must not be silent. 404 is an honest
        // "no profile yet"; anything else is a real failure to surface.
        if (profileInfo.status === 'rejected') {
          const status = (profileInfo.reason as { response?: { status?: number } })?.response
            ?.status;
          setProfileStatus(status === 404 ? 'none' : 'error');
        }

        // If profile is changes_requested, fetch CO review feedback
        if (profileInfo.status === 'fulfilled') {
          const profile = (profileInfo.value.data || profileInfo.value) as { id?: string; status?: string };
          const status = profile.status;
          setProfileStatus(status ?? null);

          if ((status === 'changes_requested' || status === 'rejected' || status === 'approved') && profile.id) {
            try {
              const review = await getReviewByProfile(profile.id);
              if (review) {
                setLatestReview(review as KYCReview);
                setReviewComments(review.comments || []);
              }
            } catch (reviewErr) {
              logger.warn('[KYCWorkspace] Failed to load review:', reviewErr);
            }
          }
        }
      } catch (err) {
        logger.error('[KYCWorkspace] Failed to initialize workspace:', err);
        if (host === 'focus') {
          onInvalid?.();
          return;
        }
        setError('Failed to load workspace data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clientId is the trigger
  }, [clientId]);

  // Close doc viewer on Escape key. capture-phase + stopPropagation so the
  // focus popup's own Esc handler doesn't ALSO pop the workspace.
  useEffect(() => {
    if (!showDocViewer) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setShowDocViewer(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [showDocViewer]);

  // Partition entries: verified → form, unverified → review strip
  const { verified: verifiedEntries, unverified: unverifiedEntries } = useMemo(
    () => partitionEntriesByVerification(knowledgeEntries),
    [knowledgeEntries]
  );

  // Navigate the wizard from the review surfaces (via bridge)
  const handleNavigateToStep = useCallback((step: number) => {
    useKYCBridge.getState().goToStep(step);
  }, []);

  // ============================================================================
  // TASK-716: dock context — the kyc scope's envelope (parity with the deleted
  // KYCChatPanel.buildKYCContext: live form snapshot read at send time).
  // ============================================================================

  useEffect(() => {
    if (!clientId) return;
    useDockStore.getState().setContextProvider(() => {
      const bridge = useKYCBridge.getState();
      return {
        current_route: `/kyc/client/${clientId}`,
        entity: { type: 'client', id: clientId, name: clientName || undefined },
        page_data: {
          client_id: clientId,
          current_section: bridge.currentSection,
          document_ids: documentIds,
          current_form_data: bridge.getFormDataSnapshotLive(),
        },
      };
    });
    return () => useDockStore.getState().setContextProvider(null);
  }, [clientId, clientName, documentIds]);

  // TASK-716 (TASK-714 contract): dirty form state must not be silently
  // discarded by Esc/✕/Back/eviction — close is a request.
  useEffect(() => {
    if (host !== 'focus') return;
    useFocusStore.getState().registerCloseGuard(() => {
      if (!useKYCBridge.getState().isDirty()) return true;
      return window.confirm(
        'You have unsaved KYC changes. Close the workspace and discard them?'
      );
    });
    return () => useFocusStore.getState().registerCloseGuard(null);
  }, [host]);

  // ============================================================================
  // Review-strip handlers (ported verbatim from the deleted KYCChatPanel)
  // ============================================================================

  // Accept handler: apply to form + verify on backend
  const handleAcceptEntries = useCallback(
    (accepted: KnowledgeGraphEntry[]) => {
      const formItems = accepted.map((e) => ({
        fieldPath: mapKGFieldToFormPath(e.field_path),
        value: cleanKGValue(e.value),
      }));
      useKYCBridge.getState().applyProposalsToForm(formItems);

      const entryIds = accepted.map((e) => e.id);
      batchVerifyEntries(clientId!, entryIds).then(({ succeeded, failed }) => {
        if (failed > 0) {
          logger.warn(`[KYCWorkspace] ${failed} entries failed to verify`);
        }
        toast.success(`Accepted ${succeeded} field${succeeded !== 1 ? 's' : ''}`);
        // Approved facts are the derivation's input, so approval is the moment to
        // re-derive: without this the RM approves a document's facts and the DNA card
        // never changes, because nothing ever proposed the `values.*` rows it reads.
        // Derived items are LLM inferences ABOUT the approved facts, so they arrive as
        // Apply/Skip proposals in the copilot — verification of a fact is not consent
        // to a conclusion drawn from it.
        if (succeeded > 0) {
          requestPrompt(DERIVE_DNA_PROMPT);
          toast.info('Deriving DNA from the approved facts — review the proposals in the copilot');
        }
      }).catch((err) => {
        logger.error('[KYCWorkspace] Batch verify failed:', err);
        toast.error('Failed to save verification status');
      });
    },
    [clientId, requestPrompt]
  );

  // Reject handler: visual only, no backend call
  const handleRejectEntries = useCallback(
    (rejected: KnowledgeGraphEntry[]) => {
      toast.info(`Rejected ${rejected.length} field${rejected.length !== 1 ? 's' : ''} — will reappear on next visit`);
    },
    []
  );

  // TASK-617: staged transition proposals
  const [stagedBusy, setStagedBusy] = useState(false);
  // Same-tick double-clicks fire before the busy re-render lands; the ref
  // guard keeps the second click from POSTing.
  const stagedInFlight = useRef(false);
  // TASK-618: transcript evidence panel for meeting-sourced proposals
  const [evidenceFor, setEvidenceFor] = useState<StagedProposal | null>(null);
  const stagedTransitions = useMemo(
    () => stagedProposals.filter((p) => p.proposal_type === 'transition'),
    [stagedProposals]
  );
  // TASK-928: `field` proposals are now the bulk of what arrives here — every claim read out
  // of an accepted document, and every life event an email triage flags. The strip rendered
  // transitions only, so those had no card and no Accept, and did not even count toward
  // opening the strip: staged and then invisible, which is worse than the direct write they
  // replaced.
  const stagedFields = useMemo(
    () => stagedProposals.filter((p) => p.proposal_type === 'field'),
    [stagedProposals]
  );

  // Current flat values per domain so the card can render its "Ends" row.
  const currentFieldsByDomain = useMemo(() => {
    const byDomain: Record<string, Record<string, unknown>> = {};
    for (const e of [...verifiedEntries, ...unverifiedEntries]) {
      const [domain, ...rest] = e.field_path.split('.');
      const leaf = rest.join('.');
      if (!leaf || leaf.startsWith('history[')) continue;
      (byDomain[domain] ??= {})[leaf] = cleanKGValue(e.value);
    }
    return byDomain;
  }, [verifiedEntries, unverifiedEntries]);

  const handleAcceptTransition = useCallback(
    async (proposalId: string, overrides?: { valid_from?: string }) => {
      if (stagedInFlight.current) return;
      stagedInFlight.current = true;
      setStagedBusy(true);
      try {
        const res = await acceptStagedProposal(proposalId, overrides);
        const followUps = res.follow_up_task_ids?.length ?? 0;
        const reconfirm = res.needs_reconfirm?.length ?? 0;
        const riskInvalidated = res.risk_answers_invalidated?.length ?? 0;
        toast.success(
          `Transition accepted — history archived` +
            (reconfirm ? `, ${reconfirm} field${reconfirm !== 1 ? 's' : ''} to re-confirm` : '') +
            (followUps ? `, ${followUps} follow-up task${followUps !== 1 ? 's' : ''} created` : '') +
            (riskInvalidated
              ? `, ${riskInvalidated} risk answer${riskInvalidated !== 1 ? 's' : ''} back to review`
              : '')
        );
        void refreshKnowledge();
      } catch (err) {
        logger.error('[KYCWorkspace] Transition accept failed:', err);
        toast.error('Failed to accept the transition');
      } finally {
        stagedInFlight.current = false;
        setStagedBusy(false);
      }
    },
    [refreshKnowledge]
  );

  const handleAcceptField = useCallback(
    async (proposalId: string) => {
      if (stagedInFlight.current) return;
      stagedInFlight.current = true;
      setStagedBusy(true);
      try {
        await acceptStagedProposal(proposalId);
        toast.success('Accepted — written to the client record');
        void refreshKnowledge();
      } catch (err) {
        logger.error('[KYCWorkspace] Field proposal accept failed:', err);
        toast.error('Failed to accept the proposal');
      } finally {
        stagedInFlight.current = false;
        setStagedBusy(false);
      }
    },
    [refreshKnowledge]
  );

  const handleDismissStaged = useCallback(
    async (proposalId: string) => {
      if (stagedInFlight.current) return;
      stagedInFlight.current = true;
      setStagedBusy(true);
      try {
        await dismissStagedProposal(proposalId);
        toast.info('Proposal dismissed — the decision is kept in the audit trail');
        void refreshKnowledge();
      } catch (err) {
        logger.error('[KYCWorkspace] Dismiss failed:', err);
        toast.error('Failed to dismiss the proposal');
      } finally {
        stagedInFlight.current = false;
        setStagedBusy(false);
      }
    },
    [refreshKnowledge]
  );

  // ============================================================================
  // Render States
  // ============================================================================

  if (!clientId) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Missing Client ID
          </h2>
          <p className="text-gray-600 mb-4">
            No client ID was provided in the URL.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 mx-auto text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${host === 'focus' ? 'h-full' : 'h-screen'} bg-gray-50`}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Loading KYC Workspace
          </h2>
          <p className="text-gray-600">
            Fetching client data and knowledge graph...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center ${host === 'focus' ? 'h-full' : 'h-screen'} bg-gray-50`}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Failed to Load Workspace
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Main Layout — full-width form + review strip (TASK-716)
  // ============================================================================

  const hasReviewContent =
    unverifiedEntries.length > 0 ||
    stagedTransitions.length > 0 ||
    stagedFields.length > 0 ||
    ((profileStatus === 'changes_requested' || profileStatus === 'rejected') && !!latestReview);

  return (
    <div className={`flex flex-col ${host === 'focus' ? 'h-full' : 'h-[calc(100vh-64px)]'}`}>
      {host === 'route' && <SecondaryNav />}
      <div className="flex flex-1 min-h-0 w-full flex-col overflow-hidden bg-gray-100">
        {/* Toolbar: documents + dossier + profile-status banners */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-3 py-2">
          <button
            onClick={() => setShowDocViewer(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <FileText className="w-4 h-4" />
            View Documents
            {documentIds.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                {documentIds.length}
              </span>
            )}
          </button>
          {/* TASK-595: render the KG into the first-party KYC dossier .docx */}
          <button
            onClick={generateDossier}
            disabled={generatingDossier}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            {generatingDossier ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            {generatingDossier ? 'Generating dossier…' : 'Generate dossier'}
          </button>

          {/* The derivation the accept path fires automatically, kept reachable on its
              own: an RM whose facts were approved earlier (or approved elsewhere) still
              needs a way to run it, and after an Accept-all the review strip that would
              otherwise host this button is gone. */}
          <button
            onClick={() => {
              requestPrompt(DERIVE_DNA_PROMPT);
              toast.info('Deriving DNA — review the proposals in the copilot');
            }}
            disabled={knowledgeEntries.length === 0}
            title={
              knowledgeEntries.length === 0
                ? 'No client facts yet — upload a document or run a meeting first'
                : "Propose the client's DNA from their knowledge graph"
            }
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="kyc-derive-dna"
          >
            <Sparkles className="w-4 h-4" />
            Derive DNA
          </button>

          {/* TASK-602: profile fetch outcome is visible, never silent. */}
          {profileStatus === 'none' && (
            <span className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
              No KYC profile yet — confirm extracted answers to start one.
            </span>
          )}
          {profileStatus === 'error' && (
            <span className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700">
              Couldn't load the KYC profile — review status may be stale.
            </span>
          )}

          {hasReviewContent && (
            <button
              onClick={() => setReviewStripOpen((v) => !v)}
              className="ml-auto flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
              data-testid="kyc-review-strip-toggle"
            >
              {reviewStripOpen ? <PanelRightClose className="h-3.5 w-3.5" /> : <PanelRightOpen className="h-3.5 w-3.5" />}
              Review
              {(unverifiedEntries.length > 0 || stagedTransitions.length > 0 || stagedFields.length > 0) && (
                <span className="rounded-full bg-blue-100 px-1.5 py-0.5">
                  {unverifiedEntries.length + stagedTransitions.length + stagedFields.length}
                </span>
              )}
            </button>
          )}
        </div>

        {/* KYC form + TASK-716 review sidebar (hideable via the Review toggle) */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          <div className={`flex-1 min-h-0 overflow-hidden ${host === 'route' ? 'pb-14' : ''}`}>
            <KYCFormPanel
              clientId={clientId}
              clientName={clientName}
              knowledgeEntries={verifiedEntries}
              totalExtracted={knowledgeEntries.length}
              latestReview={latestReview}
              reviewComments={reviewComments}
              profileStatus={profileStatus}
              extraction={extractionStatus}
              onRetryExtraction={handleRetryExtraction}
              retryingIds={retryingIds}
            />
          </div>

          {hasReviewContent && reviewStripOpen && (
            <aside
              className="w-[420px] shrink-0 space-y-3 overflow-y-auto border-l border-gray-200 bg-gray-50 px-3 py-3"
              data-testid="kyc-review-strip"
            >
              {(profileStatus === 'changes_requested' || profileStatus === 'rejected') && latestReview && (
                <COReviewTab
                  review={latestReview}
                  comments={reviewComments}
                  onNavigateToStep={handleNavigateToStep}
                />
              )}
              {stagedTransitions.map((p) => (
                <TransitionProposalCard
                  key={p.id}
                  proposal={p}
                  affectsChips={transitionAffects[p.domain] ?? []}
                  currentFields={currentFieldsByDomain[p.domain] ?? {}}
                  onAccept={handleAcceptTransition}
                  onDismiss={handleDismissStaged}
                  onShowEvidence={setEvidenceFor}
                  busy={stagedBusy}
                />
              ))}
              {stagedFields.map((p) => (
                <FieldProposalCard
                  key={p.id}
                  proposal={p}
                  onAccept={handleAcceptField}
                  onDismiss={handleDismissStaged}
                  busy={stagedBusy}
                />
              ))}
              {evidenceFor?.source_ref?.meeting_id && (
                <TranscriptEvidencePanel
                  meetingId={evidenceFor.source_ref.meeting_id}
                  quote={evidenceFor.source_ref.quote}
                  utteranceSeq={evidenceFor.source_ref.utterance_seq}
                  onClose={() => setEvidenceFor(null)}
                />
              )}
              {unverifiedEntries.length > 0 && (
                <InitialProposalReview
                  entries={unverifiedEntries}
                  clientId={clientId}
                  onAccept={handleAcceptEntries}
                  onReject={handleRejectEntries}
                />
              )}
            </aside>
          )}
        </div>

        {/* Document Viewer Modal */}
        {showDocViewer && (
          <div
            className="fixed inset-0 z-50 bg-black/50"
            onClick={() => setShowDocViewer(false)}
          >
            <div
              className="absolute inset-4 bg-white rounded-lg shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Documents
                  </h3>
                  {documentIds.length > 0 && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                      {documentIds.length}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowDocViewer(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Document viewer content */}
              <div className="flex-1 overflow-hidden">
                <DocumentViewerPanel
                  clientId={clientId}
                  documentIds={documentIds}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KYCWorkspace;
